(function () {
  'use strict';

  const MOBILE_MAX = 749;
  const TABLET_MAX = 989;
  const MOBILE_LOOP_SETS = 3;
  const MOBILE_SNAP_MS = 180;

  if (customElements.get('ingredient-history')) return;

  customElements.define(
    'ingredient-history',
    class IngredientHistory extends HTMLElement {
      constructor() {
        super();
        this.activeRealIndex = 0;
        this.lastActiveDom = -1;
        this.originalCount = 0;
        this.middleSetStart = 0;
        this.activeCategory = '';
        this.categoryItems = {};
        this.allOriginals = [];
        this.isDragging = false;
        this.isReady = false;
        this.isJumping = false;
        this.isProgrammaticScroll = false;
        this.isSettling = false;
        this.isTouching = false;
        this.scrollTimeout = null;
        this.resizeTimeout = null;
        this.programmaticTimeout = null;
        this.scrollRaf = null;
        this.dragStartX = 0;
        this.dragScrollLeft = 0;
        this.didDrag = false;
        this.touchStartScroll = null;
        this.touchStartDom = null;
        this.touchStartX = 0;
        this.touchEndDelta = 0;

        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onTrackClick = this.onTrackClick.bind(this);
        this.onPrevClick = this.onPrevClick.bind(this);
        this.onNextClick = this.onNextClick.bind(this);
        this.onTouchStart = this.onTouchStart.bind(this);
        this.onTouchMove = this.onTouchMove.bind(this);
        this.onTouchEnd = this.onTouchEnd.bind(this);
        this.onWheel = this.onWheel.bind(this);
      }

      connectedCallback() {
        this.track = this.querySelector('[data-ingredient-track]');
        this.prevBtn = this.querySelector('[data-ingredient-prev]');
        this.nextBtn = this.querySelector('[data-ingredient-next]');
        this.progressBar = this.querySelector('[data-ingredient-progress-bar]');
        this.tabs = [...this.querySelectorAll('[data-ingredient-tab]')];

        if (!this.track) return;

        this.allOriginals = [...this.track.querySelectorAll('[data-ingredient-item]:not(.is-clone)')];
        if (!this.allOriginals.length) {
          this.track.classList.remove('is-loading');
          return;
        }

        this.groupByCategory();
        const firstVisibleTab = this.tabs.find((tab) => !tab.hidden);
        this.activeCategory =
          this.activeCategory ||
          this.normalizeCategory(firstVisibleTab?.dataset.category) ||
          Object.keys(this.categoryItems)[0] ||
          '';

        this.track.classList.add('is-loading');
        this.bindEvents();
        this.buildLoop(this.allOriginals);
        this.syncTabButtons();
        this.bootCarousel();
      }

      disconnectedCallback() {
        clearTimeout(this.scrollTimeout);
        clearTimeout(this.resizeTimeout);
        clearTimeout(this.programmaticTimeout);
        clearTimeout(this._initSafetyTimeout);
        this.cancelMobileAnimation();
        if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);

        this.prevBtn?.removeEventListener('click', this.onPrevClick);
        this.nextBtn?.removeEventListener('click', this.onNextClick);
        this.tabs?.forEach((tab) => tab.removeEventListener('click', tab._ingredientTabHandler));

        this.track?.removeEventListener('scroll', this.onScroll);
        this.track?.removeEventListener('touchstart', this.onTouchStart);
        this.track?.removeEventListener('touchmove', this.onTouchMove);
        this.track?.removeEventListener('touchend', this.onTouchEnd);
        this.track?.removeEventListener('touchcancel', this.onTouchEnd);
        this.track?.removeEventListener('pointerdown', this.onPointerDown);
        this.track?.removeEventListener('pointermove', this.onPointerMove);
        this.track?.removeEventListener('pointerup', this.onPointerUp);
        this.track?.removeEventListener('pointercancel', this.onPointerUp);
        this.track?.removeEventListener('click', this.onTrackClick);
        this.track?.removeEventListener('wheel', this.onWheel);
        window.removeEventListener('resize', this.onResize);
        this.resizeObserver?.disconnect();
        this.visibilityObserver?.disconnect();
      }

      bindEvents() {
        this.prevBtn?.addEventListener('click', this.onPrevClick);
        this.nextBtn?.addEventListener('click', this.onNextClick);

        this.tabs.forEach((tab) => {
          const handler = (event) => {
            event.preventDefault();
            this.onTabClick(tab);
          };
          tab._ingredientTabHandler = handler;
          tab.addEventListener('click', handler);
        });

        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.track.addEventListener('touchstart', this.onTouchStart, { passive: true });
        this.track.addEventListener('touchmove', this.onTouchMove, { passive: false });
        this.track.addEventListener('touchend', this.onTouchEnd, { passive: true });
        this.track.addEventListener('touchcancel', this.onTouchEnd, { passive: true });
        this.track.addEventListener('pointerdown', this.onPointerDown);
        this.track.addEventListener('pointermove', this.onPointerMove);
        this.track.addEventListener('pointerup', this.onPointerUp);
        this.track.addEventListener('pointercancel', this.onPointerUp);
        this.track.addEventListener('click', this.onTrackClick);
        this.track.addEventListener('wheel', this.onWheel, { passive: false });

        window.addEventListener('resize', this.onResize);

        this.visibilityObserver = new IntersectionObserver(
          (entries) => {
            if (!entries[0]?.isIntersecting) return;
            if (!this.isReady) return;

            const domIndex = this.lastActiveDom >= 0 ? this.lastActiveDom : this.middleSetStart;
            this.settleCarousel(domIndex);
          },
          { threshold: 0.05 }
        );
        this.visibilityObserver.observe(this);

        requestAnimationFrame(() => {
          if (!this.isReady && this.track?.classList.contains('is-loading')) {
            this.ensureBooted();
          }
        });

        this.resizeObserver = new ResizeObserver(() => {
          if (!this.isReady) return;
          this.repositionToCenter(this.lastActiveDom >= 0 ? this.lastActiveDom : this.middleSetStart);
        });
        this.resizeObserver.observe(this.track);
      }

      normalizeCategory(value) {
        return (value || '')
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      }

      groupByCategory() {
        this.categoryItems = {};

        this.allOriginals.forEach((item) => {
          const category = this.normalizeCategory(item.dataset.category);
          if (!category) return;

          if (!this.categoryItems[category]) {
            this.categoryItems[category] = [];
          }

          this.categoryItems[category].push(item);
        });

        this.hideEmptyCategoryTabs();
      }

      hideEmptyCategoryTabs() {
        if (!this.tabs?.length) return;

        let firstVisible = null;

        this.tabs.forEach((tab) => {
          const category = this.normalizeCategory(tab.dataset.category);
          const hasItems = Boolean(this.categoryItems[category]?.length);

          tab.hidden = !hasItems;
          tab.style.display = hasItems ? '' : 'none';

          if (hasItems && !firstVisible) {
            firstVisible = tab;
          }
        });

        const filters = this.querySelector('.ingredient-history__filters');
        const visibleCount = this.tabs.filter((tab) => !tab.hidden).length;

        if (filters) {
          filters.hidden = visibleCount === 0;
        }

        if (
          firstVisible &&
          (!this.activeCategory || !this.categoryItems[this.activeCategory]?.length)
        ) {
          this.activeCategory = this.normalizeCategory(firstVisible.dataset.category);
        }
      }

      ensureBooted() {
        if (this.isReady || !this.track || this._bootStarted) return;
        this.bootCarousel();
      }

      bootCarousel() {
        this._bootStarted = true;
        this.initCarousel();
        this.scheduleInitSafetyTimeout();
      }

      scheduleInitSafetyTimeout() {
        clearTimeout(this._initSafetyTimeout);
        this._initSafetyTimeout = setTimeout(() => {
          if (!this.isReady && this.track) {
            this.revealTrack();
          }
        }, 1500);
      }

      onTabClick(tab) {
        const category = this.normalizeCategory(tab.dataset.category);
        if (!category || !this.categoryItems[category]?.length) return;

        const firstItem = this.categoryItems[category][0];
        const realIndex = this.allOriginals.indexOf(firstItem);
        if (realIndex === -1) return;

        this.activeCategory = category;
        this.syncTabButtons();

        const domIndex = this.findDomForRealIndex(realIndex);
        this.scrollToDom(domIndex, true);
      }

      onPrevClick(event) {
        event.preventDefault();
        this.advance(-1);
      }

      onNextClick(event) {
        event.preventDefault();
        this.advance(1);
      }

      onTrackClick(event) {
        if (this.didDrag) {
          this.didDrag = false;
          return;
        }

        const item = event.target.closest('[data-ingredient-item]');
        if (!item || item.classList.contains('is-category-hidden') || !this.track.contains(item)) return;

        const clickedDom = this.items.indexOf(item);
        if (clickedDom === -1) return;

        const activeDom = this.getActiveDomIndex();

        if (clickedDom === activeDom) {
          const title = event.target.closest('.ingredient-history__overlay-title');
          if (title && this.isMobileLayout()) {
            this.toggleOverlay(title);
          }
          return;
        }

        event.preventDefault();
        this.scrollToDom(clickedDom, true);
      }

      toggleOverlay(title) {
        const overlay = title.closest('.ingredient-history__overlay');
        if (!overlay) return;

        const now = Date.now();
        if (
          this._lastOverlayEl === overlay &&
          this._lastOverlayToggle &&
          now - this._lastOverlayToggle < 400
        ) {
          return;
        }

        this._lastOverlayToggle = now;
        this._lastOverlayEl = overlay;

        const isOpen = overlay.classList.contains('is-active');
        this.closeOverlays();
        if (!isOpen) overlay.classList.add('is-active');
      }

      closeOverlays() {
        this.track
          ?.querySelectorAll('.ingredient-history__overlay.is-active')
          .forEach((overlay) => overlay.classList.remove('is-active'));
      }

      buildLoop(originals) {
        this.originalCount = originals.length;

        originals.forEach((item, index) => {
          item.dataset.slideIndex = String(index);
          item.classList.remove('is-clone', 'is-active', 'is-category-hidden');
        });

        originals[0]?.classList.add('is-active');

        if (this.originalCount > 1) {
          const loopSets =
            this.isMobileLayout() || this.originalCount >= 5 ? MOBILE_LOOP_SETS : 5;
          const middleSet = Math.floor(loopSets / 2);

          for (let set = 0; set < loopSets; set += 1) {
            if (set === middleSet) continue;

            const fragment = document.createDocumentFragment();

            originals.forEach((item, index) => {
              const clone = item.cloneNode(true);
              clone.classList.add('is-clone');
              clone.classList.remove('is-active', 'is-category-hidden');
              clone.dataset.slideIndex = String(index);
              clone.removeAttribute('data-shopify-editor-block');
              clone.removeAttribute('id');
              this.optimizeCloneImages(clone);
              fragment.appendChild(clone);
            });

            if (set < middleSet) {
              this.track.insertBefore(fragment, originals[0]);
            } else {
              this.track.appendChild(fragment);
            }
          }

          this.middleSetStart = middleSet * this.originalCount;
        } else {
          this.middleSetStart = 0;
        }

        this.items = [...this.track.querySelectorAll('[data-ingredient-item]:not(.is-category-hidden)')];
        this.seedMobileScrollPosition();
      }

      seedMobileScrollPosition() {
        if (!this.isMobileLayout() || !this.track || this.originalCount <= 1) return;

        this.updateLayout();
        const target = this.getScrollTargetForDom(this.middleSetStart);

        this.track.style.scrollSnapType = 'none';
        this.track.style.scrollBehavior = 'auto';
        this.track.scrollLeft = target;
      }

      optimizeCloneImages(item) {
        item.querySelectorAll('img').forEach((img) => {
          img.loading = 'lazy';
          img.decoding = 'async';
          img.removeAttribute('fetchpriority');
        });
      }

      initCarousel() {
        const startDom = this.middleSetStart;
        this.initGeneration = (this.initGeneration || 0) + 1;
        const generation = this.initGeneration;

        const finishInit = async () => {
          if (!this.track || !this.items?.length) {
            if (generation === this.initGeneration) {
              this.revealTrack();
            }
            return;
          }

          try {
            this.updateLayout();

            await Promise.race([
              Promise.all([
                this.waitForFonts(),
                this.waitForStableLayout(700),
                this.waitForMedia(),
              ]),
              new Promise((resolve) => setTimeout(resolve, 1000)),
            ]);

            if (generation !== this.initGeneration || !this.track || !this.items?.length) return;

            await this.settleCarousel(startDom);

            if (generation !== this.initGeneration) return;

            this.lastActiveDom = startDom;
            this.activeRealIndex = 0;
            this.setActiveFromCenter();
          } finally {
            if (generation !== this.initGeneration || !this.track) return;
            this.revealTrack();
          }
        };

        finishInit();
      }

      revealTrack() {
        if (!this.track) return;

        clearTimeout(this._initSafetyTimeout);

        const domIndex = this.lastActiveDom >= 0 ? this.lastActiveDom : this.middleSetStart;
        this.updateLayout();
        this.repositionToCenter(domIndex);
        this.isReady = true;
        this._bootStarted = false;
        this.track.classList.remove('is-loading');
        this.setActiveFromCenter();
        this.updateProgressBar();
      }

      getActiveDomIndex() {
        return this.isMobileLayout() ? this.getLeadingDomIndex() : this.getCenteredDomIndex();
      }

      getScrollTargetForDom(domIndex) {
        const item = this.items[domIndex];
        if (!item || !this.track) return 0;

        if (this.isMobileLayout()) {
          const gutter = this.getMobileGutter();
          return Math.max(0, item.offsetLeft - gutter);
        }

        const trackWidth = this.track.clientWidth;
        const itemWidth = item.offsetWidth;
        return Math.max(0, item.offsetLeft - (trackWidth - itemWidth) / 2);
      }

      repositionToCenter(domIndex) {
        const item = this.items[domIndex];
        if (!item || !this.track) return;

        this.updateLayout();

        const targetScroll = this.getScrollTargetForDom(domIndex);
        const prevSnap = this.track.style.scrollSnapType;
        const prevBehavior = this.track.style.scrollBehavior;

        this.track.style.scrollSnapType = 'none';
        this.track.style.scrollBehavior = 'auto';
        this.track.scrollLeft = targetScroll;

        if (this.isMobileLayout()) {
          return;
        }

        requestAnimationFrame(() => {
          this.track.scrollLeft = targetScroll;
          this.track.style.scrollSnapType = prevSnap || '';
          this.track.style.scrollBehavior = prevBehavior || '';
        });
      }

      async settleCarousel(domIndex) {
        for (let attempt = 0; attempt < 3; attempt += 1) {
          this.repositionToCenter(domIndex);
          await new Promise((resolve) => requestAnimationFrame(resolve));

          const target = this.getScrollTargetForDom(domIndex);
          if (Math.abs(this.track.scrollLeft - target) < 3) break;
        }
      }

      waitForFonts() {
        if (document.fonts?.ready) {
          return document.fonts.ready.catch(() => undefined);
        }

        return Promise.resolve();
      }

      waitForStableLayout(timeoutMs = 700) {
        return new Promise((resolve) => {
          const started = performance.now();

          const check = () => {
            this.updateLayout();

            const anchorItem = this.items[this.middleSetStart];
            const trackWidth = this.getTrackWidth();
            const itemWidth =
              anchorItem?.offsetWidth ||
              parseFloat(getComputedStyle(this).getPropertyValue('--ingredient-width')) ||
              0;

            if (trackWidth > 0 && itemWidth > 0) {
              resolve();
              return;
            }

            if (performance.now() - started >= timeoutMs) {
              resolve();
              return;
            }

            requestAnimationFrame(check);
          };

          check();
        });
      }

      getTrackWidth() {
        return this.track?.clientWidth || window.innerWidth || 0;
      }

      waitForMedia() {
        const images = [
          ...this.track.querySelectorAll(
            '[data-ingredient-item]:not(.is-clone):not(.is-category-hidden) img'
          ),
        ];

        if (!images.length) {
          return Promise.resolve();
        }

        const withTimeout = (promise, ms) =>
          Promise.race([promise, new Promise((resolve) => setTimeout(resolve, ms))]);

        return withTimeout(
          Promise.all(
            images.map((img) => {
              if (img.complete) return Promise.resolve();

              return new Promise((resolve) => {
                img.addEventListener('load', resolve, { once: true });
                img.addEventListener('error', resolve, { once: true });
              });
            })
          ),
          800
        );
      }

      getItemsVisible() {
        const width = window.innerWidth;

        if (width <= MOBILE_MAX) {
          return parseFloat(this.dataset.itemsMobile) || 1.15;
        }

        if (width <= TABLET_MAX) {
          return parseFloat(this.dataset.itemsTablet) || 2.5;
        }

        return parseFloat(this.dataset.itemsDesktop) || 3.5;
      }

      isMobileLayout() {
        return window.innerWidth <= MOBILE_MAX;
      }

      getEdgeGutter() {
        const outer = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--outer'));
        if (!Number.isNaN(outer) && outer > 0) return outer;

        const wrapper = this.closest('section')?.querySelector('[class*="wrapper"]');
        if (wrapper && this.track) {
          return Math.max(16, wrapper.getBoundingClientRect().left);
        }

        return 16;
      }

      getMobileGutter() {
        if (!this.track) return this.getEdgeGutter();

        return (
          parseFloat(getComputedStyle(this.track).scrollPaddingLeft) ||
          parseFloat(getComputedStyle(this.track).paddingLeft) ||
          this.getEdgeGutter()
        );
      }

      getLeadingDomIndex() {
        if (!this.items?.length || !this.track) return 0;

        const gutter = this.getMobileGutter();
        const snapScroll = this.track.scrollLeft;
        let closestIndex = 0;
        let closestDistance = Infinity;

        this.items.forEach((item, index) => {
          if (item.classList.contains('is-category-hidden')) return;

          const itemScroll = Math.max(0, item.offsetLeft - gutter);
          const distance = Math.abs(snapScroll - itemScroll);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        return closestIndex;
      }

      getSlideStride() {
        const item =
          this.items[this.middleSetStart] ||
          this.items.find((entry) => !entry.classList.contains('is-category-hidden'));
        if (!item) return 0;

        const gap = parseFloat(getComputedStyle(this.track).gap) || 16;
        return item.offsetWidth + gap;
      }

      cancelMobileAnimation() {
        if (this._mobileAnimFrame) {
          cancelAnimationFrame(this._mobileAnimFrame);
          this._mobileAnimFrame = null;
        }

        clearTimeout(this._mobileSnapFallback);
        this._mobileSnapFallback = null;
      }

      animateScrollTo(targetLeft, duration = MOBILE_SNAP_MS) {
        this.cancelMobileAnimation();

        return new Promise((resolve) => {
          const start = this.track.scrollLeft;
          const distance = targetLeft - start;

          if (Math.abs(distance) < 2) {
            this.track.scrollLeft = targetLeft;
            resolve();
            return;
          }

          const startTime = performance.now();

          const step = (now) => {
            const progress = Math.min((now - startTime) / duration, 1);
            const eased = 1 - (1 - progress) ** 3;

            this.track.scrollLeft = start + distance * eased;
            this.syncActiveClass(this.getActiveDomIndex());
            this.updateProgressBar();

            if (progress < 1) {
              this._mobileAnimFrame = requestAnimationFrame(step);
              return;
            }

            this.track.scrollLeft = targetLeft;
            this._mobileAnimFrame = null;
            resolve();
          };

          this._mobileAnimFrame = requestAnimationFrame(step);
        });
      }

      onTouchStart(event) {
        if (!this.isMobileLayout() || !this.isReady || this.isSettling) return;
        if (
          event.target.closest(
            '[data-ingredient-prev], [data-ingredient-next], [data-ingredient-tab]'
          )
        ) {
          return;
        }

        this.cancelMobileAnimation();
        this.isProgrammaticScroll = false;
        this.isSettling = false;
        this.track.classList.remove('is-settling');
        this.track.style.scrollBehavior = '';

        this.isTouching = true;
        this.touchStartX = event.touches[0].clientX;
        this.touchStartScroll = this.track.scrollLeft;
        this.touchStartDom =
          this.lastActiveDom >= 0 ? this.lastActiveDom : this.getLeadingDomIndex();
        this.touchEndDelta = 0;
        this.track.classList.add('is-touching');
      }

      onTouchMove(event) {
        if (!this.isTouching || !this.isMobileLayout()) return;

        event.preventDefault();

        const delta = this.touchStartX - event.touches[0].clientX;
        const stride = this.getSlideStride();
        const maxDrag = stride * 0.92;
        const clamped = Math.max(-maxDrag, Math.min(maxDrag, delta));

        this.track.scrollLeft = this.touchStartScroll + clamped;
        this.touchEndDelta = clamped;
        this.updateProgressBar();
      }

      onTouchEnd(event) {
        if (!this.isTouching || !this.isMobileLayout()) return;

        if (event.changedTouches?.[0]) {
          this.touchEndDelta = this.touchStartX - event.changedTouches[0].clientX;
        }

        this.isTouching = false;
        this.track.classList.remove('is-touching');

        if (this.isSettling) return;

        const stride = this.getSlideStride();
        const isTap = !stride || Math.abs(this.touchEndDelta) < Math.min(10, stride * 0.05);

        if (isTap) {
          const title = event.target.closest?.('.ingredient-history__overlay-title');
          const item = event.target.closest?.('[data-ingredient-item]');

          if (title && item && this.items.indexOf(item) === this.getActiveDomIndex()) {
            this.toggleOverlay(title);
          }
        }

        const targetDom = this.getMobileSnapTarget();
        this.snapMobileToDom(targetDom);
      }

      getMobileSnapTarget() {
        const startDom =
          this.touchStartDom ??
          (this.lastActiveDom >= 0 ? this.lastActiveDom : this.middleSetStart);
        const stride = this.getSlideStride();
        const delta =
          this.touchEndDelta || this.track.scrollLeft - (this.touchStartScroll ?? 0);

        if (!stride || Math.abs(delta) < stride * 0.1) {
          return startDom;
        }

        return startDom + (delta > 0 ? 1 : -1);
      }

      clampDomIndex(domIndex) {
        if (!this.items?.length) return 0;
        return Math.max(0, Math.min(this.items.length - 1, domIndex));
      }

      snapMobileToDom(targetDom) {
        const safeDom = this.clampDomIndex(targetDom);

        if (!this.items[safeDom] || this.items[safeDom].classList.contains('is-category-hidden')) {
          this.afterMobileSnap();
          return;
        }

        const targetScroll = this.getScrollTargetForDom(safeDom);
        const drift = Math.abs(this.track.scrollLeft - targetScroll);

        if (drift <= 2) {
          this.afterMobileSnap();
          return;
        }

        this.isSettling = true;
        this.isProgrammaticScroll = true;
        this.track.style.scrollSnapType = 'none';
        this.track.style.scrollBehavior = 'auto';
        this.track.classList.add('is-settling');

        this.animateScrollTo(targetScroll, MOBILE_SNAP_MS).then(() => {
          this.withoutScrollAnimation(() => {
            this.silentReposition();
          });
          this.track.style.scrollSnapType = '';
          this.track.style.scrollBehavior = '';
          this.track.classList.remove('is-settling');
          this.isProgrammaticScroll = false;
          this.afterMobileSnap();
        });
      }

      afterMobileSnap() {
        const previousDom = this.lastActiveDom;
        this.lastActiveDom = this.getActiveDomIndex();

        if (previousDom !== this.lastActiveDom) {
          this.closeOverlays();
        }

        this.setActiveFromCenter();
        this.updateProgressBar();
        this.touchStartScroll = null;
        this.touchStartDom = null;
        this.touchEndDelta = 0;

        requestAnimationFrame(() => {
          this.isSettling = false;
        });
      }

      updateProgressBar() {
        if (!this.progressBar || !this.originalCount || !this.isMobileLayout()) return;

        const domIndex = this.getActiveDomIndex();
        const realIndex = parseInt(this.items[domIndex]?.dataset.slideIndex, 10) || 0;

        const thumbWidth = Math.min(100, Math.max(18, (1 / this.originalCount) * 100));
        const maxOffset = 100 - thumbWidth;
        const offset =
          this.originalCount > 1 ? (realIndex / (this.originalCount - 1)) * maxOffset : 0;

        this.progressBar.style.width = `${thumbWidth}%`;
        this.progressBar.style.left = `${offset}%`;
      }

      updateLayout() {
        const trackWidth = this.getTrackWidth();
        if (!trackWidth) return;

        const itemsVisible = this.getItemsVisible();
        const gap =
          parseFloat(getComputedStyle(this).getPropertyValue('--ingredient-gap')) ||
          parseFloat(getComputedStyle(this.track).gap) ||
          16;
        const itemWidth = (trackWidth - gap * (itemsVisible - 1)) / itemsVisible;

        this.style.setProperty('--ingredient-width', `${Math.max(itemWidth, 100)}px`);

        if (this.isMobileLayout()) {
          const gutter = this.getEdgeGutter();
          this.track.style.paddingLeft = `${gutter}px`;
          this.track.style.paddingRight = `${gutter}px`;
          this.track.style.scrollPaddingLeft = `${gutter}px`;
          this.track.style.scrollPaddingRight = `${gutter}px`;
          this.track.classList.add('is-mobile-start');
          return;
        }

        this.track.classList.remove('is-mobile-start');
        const sidePadding = Math.max(0, (trackWidth - itemWidth) / 2);

        this.track.style.paddingLeft = `${sidePadding}px`;
        this.track.style.paddingRight = `${sidePadding}px`;
        this.track.style.scrollPaddingLeft = `${sidePadding}px`;
        this.track.style.scrollPaddingRight = `${sidePadding}px`;
      }

      getSetWidth() {
        const start = this.items[this.middleSetStart];
        const next = this.items[this.middleSetStart + this.originalCount];

        if (start && next) {
          return next.offsetLeft - start.offsetLeft;
        }

        const gap = parseFloat(getComputedStyle(this.track).gap) || 16;
        const itemWidth = this.items[0]?.offsetWidth || 0;
        return (itemWidth + gap) * this.originalCount;
      }

      getMiddleBounds() {
        return {
          start: this.middleSetStart,
          end: this.middleSetStart + this.originalCount - 1,
        };
      }

      withoutScrollAnimation(callback) {
        const prevSnap = this.track.style.scrollSnapType;
        const prevBehavior = this.track.style.scrollBehavior;

        this.isJumping = true;
        this.track.classList.add('is-jumping');
        this.track.style.scrollSnapType = 'none';
        this.track.style.scrollBehavior = 'auto';

        callback();

        requestAnimationFrame(() => {
          this.track.style.scrollSnapType = prevSnap;
          this.track.style.scrollBehavior = prevBehavior;
          this.track.classList.remove('is-jumping');
          this.isJumping = false;
        });
      }

      silentReposition() {
        if (this.originalCount <= 1) return;

        const domIndex = this.getActiveDomIndex();
        const { start, end } = this.getMiddleBounds();

        if (domIndex >= start && domIndex <= end) return;

        const setWidth = this.getSetWidth();
        if (!setWidth) return;

        this.withoutScrollAnimation(() => {
          if (domIndex < start) {
            this.track.scrollLeft += setWidth;
          } else {
            this.track.scrollLeft -= setWidth;
          }
        });

        this.lastActiveDom = this.getActiveDomIndex();
      }

      scrollDomTo(domIndex, smooth = true) {
        if (this.isMobileLayout()) {
          this.snapMobileToDom(domIndex);
          return Promise.resolve();
        }

        const targetScroll = this.getScrollTargetForDom(domIndex);

        if (!smooth || this.isJumping) {
          this.repositionToCenter(domIndex);
          return Promise.resolve();
        }

        this.isProgrammaticScroll = true;
        this.track.style.scrollSnapType = 'none';
        this.track.style.scrollBehavior = 'auto';

        return this.animateScrollTo(targetScroll, 350).then(() => {
          this.track.style.scrollSnapType = '';
          this.track.style.scrollBehavior = '';
          this.isProgrammaticScroll = false;
        });
      }

      scrollDomToCenter(domIndex, smooth = true) {
        this.scrollDomTo(domIndex, smooth);
      }

      getCenteredDomIndex() {
        if (!this.items?.length || !this.track) return 0;

        if (this.isMobileLayout()) {
          return this.getLeadingDomIndex();
        }

        const trackWidth = this.track.clientWidth;
        const centerScroll = this.track.scrollLeft + trackWidth / 2;
        let closestIndex = 0;
        let closestDistance = Infinity;

        this.items.forEach((item, index) => {
          if (item.classList.contains('is-category-hidden')) return;

          const itemCenter = item.offsetLeft + item.offsetWidth / 2;
          const distance = Math.abs(centerScroll - itemCenter);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        return closestIndex;
      }

      findDomForRealIndex(realIndex) {
        const { start, end } = this.getMiddleBounds();

        for (let i = start; i <= end; i += 1) {
          if (parseInt(this.items[i].dataset.slideIndex, 10) === realIndex) {
            return i;
          }
        }

        return this.middleSetStart + realIndex;
      }

      advance(direction) {
        if (!this.items?.length || this.originalCount <= 1) return;

        let currentDom = this.getActiveDomIndex();
        let nextDom = currentDom + direction;

        if (nextDom >= this.items.length || nextDom < 0) {
          this.silentReposition();
          currentDom = this.getActiveDomIndex();
          nextDom = currentDom + direction;
        }

        const { start, end } = this.getMiddleBounds();

        if (direction > 0 && currentDom >= end && nextDom > end) {
          this.silentReposition();
          currentDom = this.getActiveDomIndex();
          nextDom = currentDom + 1;
        }

        if (direction < 0 && currentDom <= start && nextDom < start) {
          this.silentReposition();
          currentDom = this.getActiveDomIndex();
          nextDom = currentDom - 1;
        }

        if (nextDom >= 0 && nextDom < this.items.length) {
          this.scrollToDom(nextDom, true);
        }
      }

      scrollToDom(domIndex, smooth = true) {
        if (!this.items[domIndex]) return;

        if (this.isMobileLayout()) {
          this.snapMobileToDom(domIndex);
          return;
        }

        this.syncActiveClass(domIndex);
        clearTimeout(this.programmaticTimeout);

        this.scrollDomTo(domIndex, smooth).then(() => {
          this.silentReposition();
          this.repositionToCenter(this.getActiveDomIndex());
          this.lastActiveDom = this.getActiveDomIndex();
          this.setActiveFromCenter();
        });
      }

      scrollToRealIndex(realIndex) {
        if (Number.isNaN(realIndex)) return;

        const domIndex = this.findDomForRealIndex(realIndex);
        this.scrollToDom(domIndex, true);
      }

      syncActiveClass(domIndex) {
        this.items.forEach((item, index) => {
          item.classList.toggle('is-active', index === domIndex);
        });

        this.syncTabsFromDom(domIndex);
      }

      syncTabsFromDom(domIndex) {
        const category = this.normalizeCategory(this.items?.[domIndex]?.dataset.category);
        if (!category || category === this.activeCategory) return;

        this.activeCategory = category;
        this.syncTabButtons();
      }

      syncTabButtons() {
        this.tabs.forEach((tab) => {
          const tabCategory = this.normalizeCategory(tab.dataset.category);
          const isActive = tabCategory === this.activeCategory;

          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-selected', String(isActive));
        });

        this.centerActiveTab();
      }

      centerActiveTab() {
        const active = this.tabs.find((tab) => tab.classList.contains('is-active'));
        const container = active?.closest('.ingredient-history__filters');
        if (!active || !container) return;

        const maxScroll = container.scrollWidth - container.clientWidth;
        if (maxScroll <= 1) return;

        const target = active.offsetLeft - (container.clientWidth - active.offsetWidth) / 2;
        container.scrollLeft = Math.max(0, Math.min(target, maxScroll));
      }

      setActiveFromCenter() {
        const domIndex = this.getActiveDomIndex();
        const realIndex = parseInt(this.items[domIndex]?.dataset.slideIndex, 10);

        if (Number.isNaN(realIndex)) return;

        this.activeRealIndex = realIndex;
        this.lastActiveDom = domIndex;
        this.syncActiveClass(domIndex);
        this.updateProgressBar();
      }

      onScroll() {
        if (!this.isReady || this.isJumping) return;

        const rect = this.track.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;

        if (this.isMobileLayout()) {
          if (!this.scrollRaf) {
            this.scrollRaf = requestAnimationFrame(() => {
              this.scrollRaf = null;
              if (this.isProgrammaticScroll) {
                this.syncActiveClass(this.getActiveDomIndex());
              }
              this.updateProgressBar();
            });
          }
          return;
        }

        if (this.isDragging || this.isProgrammaticScroll || this.isSettling) return;

        clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => {
          this.silentReposition();
          this.setActiveFromCenter();
          this.updateProgressBar();
        }, 120);
      }

      onResize() {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
          if (this.lastActiveDom >= 0) {
            this.repositionToCenter(this.lastActiveDom);
          }
          this.setActiveFromCenter();
          this.updateProgressBar();
        }, 150);
      }

      onPointerDown(event) {
        if (event.target.closest('[data-ingredient-prev], [data-ingredient-next], [data-ingredient-tab]')) {
          return;
        }

        if (event.pointerType !== 'mouse' || event.button !== 0) return;

        this.isDragging = true;
        this.didDrag = false;
        this.dragStartX = event.clientX;
        this.dragScrollLeft = this.track.scrollLeft;
        this.track.classList.add('is-dragging');
        this.track.setPointerCapture(event.pointerId);
      }

      onPointerMove(event) {
        if (!this.isDragging) return;

        const delta = event.clientX - this.dragStartX;
        if (Math.abs(delta) > 5) {
          this.didDrag = true;
        }

        this.track.scrollLeft = this.dragScrollLeft - delta;
      }

      onPointerUp(event) {
        if (!this.isDragging) return;

        this.isDragging = false;
        this.track.classList.remove('is-dragging');

        if (this.track.hasPointerCapture(event.pointerId)) {
          this.track.releasePointerCapture(event.pointerId);
        }

        if (!this.isMobileLayout()) {
          clearTimeout(this.scrollTimeout);
          this.scrollTimeout = setTimeout(() => {
            const domIndex = this.getCenteredDomIndex();
            this.scrollToDom(domIndex, true);
          }, 100);
        }
      }

      onWheel(event) {
        if (this.isMobileLayout()) return;
        if (Math.abs(event.deltaX) > 0 || event.shiftKey) {
          event.preventDefault();
        }
      }
    }
  );

  document.addEventListener('shopify:block:select', (event) => {
    const tab = event.target.closest?.('[data-ingredient-tab]');
    if (tab) {
      const root = tab.closest('ingredient-history');
      if (root?.categoryItems) {
        root.onTabClick(tab);
      }
      return;
    }

    const root = event.target.closest('ingredient-history');
    if (!root || !root.items?.length) return;

    const item = event.target.closest('[data-ingredient-item]');
    if (!item || item.classList.contains('is-clone')) return;

    const slideIndex = parseInt(item.dataset.slideIndex, 10);

    if (!Number.isNaN(slideIndex)) {
      root.scrollToRealIndex(slideIndex);
    }
  });
})();
